import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import '@timelyapp/design-system/theme-timely-light.css';
import '@timelyapp/design-system/theme-timely-dark.css';
import '../styles/define-theme-variables.scss';

import.meta.hot?.accept('@timelyapp/design-system/theme-timely-light.css', () => {
  console.log('Hot update @timelyapp/design-system/theme-timely-light.css');
});

const THEME_STORAGE_KEY = 'timely-theme';

type Theme = 'timely-light' | 'timely-dark';
export type ThemePreference = 'system' | 'light' | 'dark';

interface ThemeInfo {
  dark: boolean;
}
const themeVariants: Record<Theme, ThemeInfo> = {
  'timely-light': { dark: false },
  'timely-dark': { dark: true },
};

function getSystemTheme(): Theme {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
  ) {
    return 'timely-dark';
  }
  return 'timely-light';
}

function resolveTheme(pref: ThemePreference): Theme {
  if (pref === 'light') return 'timely-light';
  if (pref === 'dark') return 'timely-dark';
  return getSystemTheme();
}

function getStoredPreference(): ThemePreference | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'system' || stored === 'light' || stored === 'dark') {
      return stored;
    }
    // Migrate old stored values from the two-state toggle
    if (stored === 'timely-dark') return 'dark';
    if (stored === 'timely-light') return 'light';
  } catch {
    // localStorage not available
  }
  return null;
}

function storePreference(pref: ThemePreference) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // localStorage not available
  }
}

interface ThemeContextValue {
  theme: Theme;
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = React.createContext<ThemeContextValue>({
  theme: 'timely-light',
  preference: 'system',
  setPreference: () => {},
});
ThemeContext.displayName = 'ThemeContext';

interface ThemeProviderProps {
  children?: React.ReactNode;
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [preference, _setPreference] = React.useState<ThemePreference>(
    () => getStoredPreference() ?? 'system'
  );
  const [theme, setTheme] = React.useState<Theme>(() => resolveTheme(preference));

  const setPreference = useCallback((next: ThemePreference) => {
    storePreference(next);
    _setPreference(next);
    setTheme(resolveTheme(next));
  }, []);

  // Re-resolve when system preference changes, but only if the user chose "system"
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mql) return;
    const handler = (e: MediaQueryListEvent) => {
      // Read from localStorage to get the latest preference
      const current = getStoredPreference() ?? 'system';
      if (current === 'system') {
        setTheme(e.matches ? 'timely-dark' : 'timely-light');
      }
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useThemeBodyClass(theme);

  const value = React.useMemo(
    () => ({ theme, preference, setPreference }),
    [theme, preference, setPreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeBodyClass = (theme: Theme) => {
  useLayoutEffect(() => {
    Array.from(document.documentElement.classList).forEach(cls => {
      if (cls.startsWith('theme-')) {
        document.documentElement.classList.remove(cls);
      }
    });
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);
};

export const useCurrentThemeContext = () => React.useContext(ThemeContext);

export const useCurrentThemeIsDark = () => {
  return themeVariants[useCurrentTheme()].dark;
};

export const useCurrentTheme = () => useCurrentThemeContext().theme;

export const themeClassName = (theme: Theme) => `theme-${theme}`;
